import { ApiError } from '../../api/apiClient';
import {
    ExportUnsupportedError,
    type TraderSearchRequest,
} from '../../api/traderSearch';
import {
    MIME,
    aborted,
    withRetry,
    type ExportProgress,
    type ExportResult,
} from '../../event/export/exportRun';
import type { ExportFile } from '../../event/export/exportTypes';

// The trader download: every trader matching the current filters, in the
// current sort order, loaded as text pages of 10,000 rows. The indexer writes
// the CSV lines or JSON lines itself, so no row is ever parsed here; each page
// goes into a Blob as soon as it arrives and the file is the Blobs in order.
// There is no row limit: the filters decide how much is downloaded.

export const TRADER_EXPORT_PAGE_SIZE = 10_000;
// Two pages at a time: the indexer runs two advanced queries per pod.
export const TRADER_EXPORT_WORKERS = 2;
// Above this the popover says that the download takes several minutes.
export const TRADER_EXPORT_LARGE = 500_000;

export interface TraderExportPage {
    offset: number;
    limit: number;
}

export function traderExportPages(total: number): TraderExportPage[] {
    const pages: TraderExportPage[] = [];
    for (let offset = 0; offset < total; offset += TRADER_EXPORT_PAGE_SIZE) {
        pages.push({
            offset,
            limit: Math.min(TRADER_EXPORT_PAGE_SIZE, total - offset),
        });
    }
    return pages;
}

// The fields of the full download, in the order of the file. The indexer owns
// this list (TRADER_EXPORT_FIELDS in api/api_traders.py) and rejects other
// names; change both together.
export const TRADER_EXPORT_FIELDS: readonly string[] = [
    'address',
    'labels',
    'main_category',
    'first_seen',
    'last_seen',
    'active_days',
    'total_trades',
    'lifetime_volume_usd',
    'avg_size_usd',
    'trade_freq_per_day',
    'unique_markets',
    'closed_count',
    'avg_hold_days',
    'win_rate',
    'total_pnl',
    'realized_pnl',
    'roi_pct',
    'roe_pct',
    'profit_factor',
    'daily_sharpe',
    'position_sharpe',
    'max_pnl_drawdown_usd',
    'roct',
    'twr_pct',
    'balance_usd',
    'portfolio_value',
    'total_deposits',
    'total_withdrawals',
    'net_deposits',
    'algo_likelihood',
    'capital_utilization',
    'market_liquidity_score',
];

const IDENTITY_FIELDS = ['address', 'labels', 'main_category'];

// The "As shown" download (columns spec 4.7): who the trader is, then the
// fields behind the visible columns, in their order.
export function exportFieldsFor(
    columns: readonly { exportFields: readonly string[] }[]
): string[] {
    const out: string[] = [];
    for (const name of [
        ...IDENTITY_FIELDS,
        ...columns.flatMap(c => c.exportFields),
    ]) {
        if (!out.includes(name)) out.push(name);
    }
    return out;
}

export function traderExportFileName(file: ExportFile, nowMs: number): string {
    return `traders_${new Date(nowMs).toISOString().slice(0, 10)}.${file}`;
}

export interface TraderExportDeps {
    // One page of the file as the indexer writes it: CSV lines without a
    // header, or JSON lines.
    loadPage: (
        request: TraderSearchRequest,
        signal: AbortSignal
    ) => Promise<string>;
    onProgress: (progress: ExportProgress) => void;
    signal: AbortSignal;
    retryDelayMs?: number;
    isFatal?: (error: unknown) => boolean;
    nowMs?: number;
    // The fields to write, in order; every field when absent.
    fields?: readonly string[];
}

// A retry cannot help these: a bad request, no access, the server's own
// 120 s timeout (a retry would only wait another 120 s), or an indexer that
// does not know the text download.
const FATAL_STATUSES = [400, 401, 403, 404, 422, 504];

export function isFatalTraderError(error: unknown): boolean {
    return (
        error instanceof ExportUnsupportedError ||
        (error instanceof ApiError && FATAL_STATUSES.includes(error.status))
    );
}

// One line per trader. A CSV cell with an embedded newline would count twice,
// but none of the download's fields (address, labels, main category, numbers,
// timestamps) can hold one.
function countLines(text: string): number {
    let lines = 0;
    for (
        let at = text.indexOf('\n');
        at !== -1;
        at = text.indexOf('\n', at + 1)
    )
        lines += 1;
    return lines;
}

// A page is whole lines; one that lost its last newline still ends the line.
function terminated(text: string): string {
    return text === '' || text.endsWith('\n') ? text : `${text}\n`;
}

// JSON lines never hold a raw newline (JSON escapes it), so the lines of a
// page become array items by swapping each newline for a comma.
function jsonItems(text: string): string {
    return text.trimEnd().replaceAll('\n', ',');
}

export async function runTraderExport(
    request: TraderSearchRequest,
    total: number,
    file: ExportFile,
    deps: TraderExportDeps
): Promise<ExportResult> {
    // A column change during the run must not change the file being built.
    const fields = deps.fields ? [...deps.fields] : [...TRADER_EXPORT_FIELDS];
    for (const name of fields) {
        if (!TRADER_EXPORT_FIELDS.includes(name))
            throw new Error(`Unknown trader export field: ${name}`);
    }
    const pages = traderExportPages(total);
    const parts: Blob[] = new Array(pages.length);
    const lines: number[] = new Array(pages.length).fill(0);
    // One failed page stops the others: the file would be incomplete anyway.
    const stop = new AbortController();
    const forward = () => stop.abort();
    deps.signal.addEventListener('abort', forward, { once: true });
    if (deps.signal.aborted) stop.abort();

    let next = 0;
    let done = 0;
    const progress = () => {
        if (!stop.signal.aborted)
            deps.onProgress({ done, total: pages.length });
    };
    progress();

    const worker = async () => {
        while (next < pages.length && !stop.signal.aborted) {
            const index = next;
            next += 1;
            const page: TraderSearchRequest = {
                ...request,
                mode: 'advanced',
                format: file,
                fields,
                ...pages[index],
            };
            const text = await withRetry(
                signal => deps.loadPage(page, signal),
                {
                    signal: stop.signal,
                    retryDelayMs: deps.retryDelayMs,
                    isFatal: deps.isFatal ?? isFatalTraderError,
                }
            );
            const whole = terminated(text);
            lines[index] = countLines(whole);
            parts[index] = new Blob([
                file === 'csv' ? whole : jsonItems(whole),
            ]);
            done += 1;
            progress();
        }
    };

    try {
        await Promise.all(
            Array.from(
                { length: Math.min(TRADER_EXPORT_WORKERS, pages.length) },
                () =>
                    worker().catch(error => {
                        stop.abort();
                        throw error;
                    })
            )
        );
    } finally {
        deps.signal.removeEventListener('abort', forward);
    }
    // A cancel lets the workers stop between pages without an error; the
    // file would be incomplete, so it is never built.
    if (deps.signal.aborted) throw aborted();
    if (done !== pages.length) {
        throw new Error(`Loaded ${done} of ${pages.length} pages`);
    }

    // The rows counted are the rows received: the table can change while a
    // long download runs, so they may differ a little from the count asked.
    const rows = lines.reduce((sum, n) => sum + n, 0);
    const nowMs = deps.nowMs ?? Date.now();
    const items = parts.filter(part => part.size > 0);
    const blob =
        file === 'csv'
            ? new Blob([`${fields.join(',')}\n`, ...parts], { type: MIME.csv })
            : new Blob(
                  [
                      `{"downloaded_at":${JSON.stringify(new Date(nowMs).toISOString())},` +
                          `"request":${JSON.stringify(request)},` +
                          `"fields":${JSON.stringify(fields)},` +
                          `"rows":${rows},"traders":[`,
                      ...items.flatMap((part, i) =>
                          i === 0 ? [part] : [',', part]
                      ),
                      ']}',
                  ],
                  { type: MIME.json }
              );
    return { filename: traderExportFileName(file, nowMs), blob, rows };
}
